import React from 'react'
import {Box, Button, Typography} from '@mui/material'
import {mdiAlertCircle} from '@mdi/js'
import Icon from '@mdi/react'
import {useTranslation} from 'react-i18next'

export interface ErrorViewProps {
  error: Error | null
  onRetry: () => void
  onClose: () => void
  'data-test-id'?: string
}

/**
 * Generic error view component.
 * Shows error message with retry and close actions.
 */
export const ErrorView: React.FC<ErrorViewProps> = ({
  error,
  onRetry,
  onClose,
  'data-test-id': testId = 'error-view'
}) => {
  const {t} = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '16px',
        padding: '24px'
      }}
      data-test-id={testId}
    >
      <Icon path={mdiAlertCircle} size={2} color="error" />

      <Typography variant="h6">
        {t('orderManagement.loadError', 'Failed to load order options')}
      </Typography>

      <Typography variant="body2" color="text.secondary" align="center">
        {error?.message || t('orderManagement.unknownError', 'An unknown error occurred')}
      </Typography>

      <Box sx={{display: 'flex', gap: '12px', marginTop: '16px'}}>
        <Button variant="contained" onClick={onRetry} data-test-id={`${testId}-retry`}>
          {t('common.retry', 'Retry')}
        </Button>
        <Button variant="outlined" onClick={onClose} data-test-id={`${testId}-close`}>
          {t('common.close', 'Close')}
        </Button>
      </Box>
    </Box>
  )
}
